import { Locator, LocatorLocations, LocatorText, Manifest } from '@readium/shared';
import type { LocatorLike, MetadataResponse } from '../models/metadata-response';
import type { TextFoundResponse } from '../models/selections';
import { normalizeText } from './text';

export function extractFileName(href: string): string {
  return href.split('/').pop()!;
}

export function extractFileNameWithoutExtension(href: string): string {
  return extractFileName(href).replace(/\.[^/.]+$/, '');
}

/**
 * File name (no folder or extension) of each spine item, by its OPF idref. The previous NubeReader
 * saved chapters by idref (`componentId`, `idRef`, `idref`), which only matches the file name when
 * the publisher named them alike (e.g. idref `id-id1172295069667` for `ch04s01.xhtml`).
 */
export type ComponentNames = ReadonlyMap<string, string>;

/**
 * File name of the chapter a saved component id names: its idref resolved through `names`, or the
 * id itself when it is not an idref (this reader used to save file names).
 */
export function componentName(componentId: string, names?: ComponentNames): string {
  return names?.get(componentId) ?? extractFileNameWithoutExtension(componentId);
}

/** The book's positions, plus the idrefs saved data may name its chapters by. */
export interface BookChapters {
  positions: LocatorLike[];
  componentNames?: ComponentNames;
}

// Readium skips `go_progression` for 0, which would leave the reader wherever it is in the same
// chapter: the first page of a chapter needs a tiny positive progression instead.
export const CHAPTER_START_PROGRESSION = 1e-7;

export function normalizeProgression(progression: number): number {
  return Math.max(0, Math.min(1, progression));
}

/** Every position of the chapter `componentId` names, in book order. */
export function findComponentPositions(
  componentId: string,
  positions: LocatorLike[],
  names?: ComponentNames,
): LocatorLike[] {
  const name = componentName(componentId, names);
  return positions.filter(({ href }) => extractFileNameWithoutExtension(href) === name);
}

export function findComponentPosition(
  componentId: string,
  positions: LocatorLike[],
  names?: ComponentNames,
): LocatorLike | undefined {
  return findComponentPositions(componentId, positions, names)[0];
}

export function createReadium2Locator(locator: LocatorLike, positions: LocatorLike[]): Locator {
  const fileName = extractFileName(locator.href);
  const position = positions.find((p) => extractFileName(p.href) === fileName);
  if (!position) throw new Error('Position not found for locator');
  return Locator.deserialize({
    ...locator,
    href: position.href,
    locations: { ...locator.locations, position: position.locations.position },
  })!;
}

export function findChapterIndexByIdRef(
  chaptersRef: string[],
  idRef: string,
  names?: ComponentNames,
): number {
  const name = componentName(idRef, names);
  return chaptersRef.findIndex((href) => extractFileNameWithoutExtension(href) === name);
}

export function getChapterTitleByPosition(
  positions: LocatorLike[],
  position: number,
): string | undefined {
  const pos = positions.find((p) => p.locations.position === position);
  return pos?.title;
}

export function createLocatorFromSearchResult(
  searchResult: TextFoundResponse,
  { positions, componentNames }: BookChapters,
  highlightText?: string,
): Locator {
  const positionIndex = findChapterIndexByIdRef(
    positions.map((p) => p.href),
    searchResult.idRef,
    componentNames,
  );

  if (positionIndex === -1) {
    throw new Error('Position not found for search result');
  }

  const progression = normalizeProgression(searchResult.position);

  let highlight = highlightText;
  let before = '';
  let after = '';

  if (highlightText && searchResult.context) {
    const contextLower = searchResult.context.toLowerCase();
    const highlightLower = highlightText.toLowerCase();
    const highlightIndex = contextLower.indexOf(highlightLower);

    if (highlightIndex !== -1) {
      before = normalizeText(searchResult.context.substring(0, highlightIndex));
      highlight = searchResult.context.substring(
        highlightIndex,
        highlightIndex + highlightText.length,
      );
      after = normalizeText(searchResult.context.substring(highlightIndex + highlightText.length));
    }
  }

  return new Locator({
    href: positions[positionIndex].href,
    type: positions[positionIndex].type || 'application/xhtml+xml',
    locations: new LocatorLocations({
      position: positions[positionIndex].locations.position,
      progression,
    }),
    ...(highlight && { text: new LocatorText({ highlight, before, after }) }),
  });
}

export function createLocatorForPosition(
  position: number,
  progression: number,
  metadataResponse: MetadataResponse,
): Locator | null {
  if (position < 1 || position > metadataResponse.spine.length) {
    return null;
  }

  const manifest = Manifest.deserialize(metadataResponse.readium2.manifest)!;
  const item = manifest.readingOrder.items[position - 1];

  return new Locator({
    href: item.href,
    type: item.type ?? 'application/xhtml+xml',
    locations: new LocatorLocations({
      position,
      progression: normalizeProgression(progression),
    }),
  });
}

export function locatorSafeSerialize(locator: Locator): LocatorLike {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return locator.serialize();
}
